import React, { useState, useEffect, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import {
  Wallet, GraduationCap, Briefcase, Users, Home, PlusCircle,
  Save, Lightbulb, Utensils, Bus, HeartPulse, Landmark, ShoppingBag, Info,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { UI, t } from "@/i18n/translations";
import { api } from "@/lib/api";

const FIELDS = [
  { key: "bourse", Icon: GraduationCap },
  { key: "job", Icon: Briefcase },
  { key: "famille", Icon: Users },
  { key: "apl", Icon: Home },
  { key: "autres", Icon: PlusCircle },
];

const NEEDS = [
  { key: "loyer", pct: 0.55, Icon: Home, color: "#22314F" },
  { key: "alimentation", pct: 0.2, Icon: Utensils, color: "#E8A33D" },
  { key: "transport", pct: 0.08, Icon: Bus, color: "#3E8E72" },
  { key: "telecom", pct: 0.06, Icon: Info, color: "#22314F" },
  { key: "sante", pct: 0.06, Icon: HeartPulse, color: "#C4553D" },
  { key: "divers", pct: 0.05, Icon: Landmark, color: "#8a93a8" },
];

export default function Budget() {
  const { lang } = useLang();
  const B = UI.budgetMod;
  const [form, setForm] = useState({ bourse: "", job: "", famille: "", apl: "", autres: "" });
  const [saving, setSaving] = useState(false);

  const money = useMemo(
    () => new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }),
    [lang]
  );

  useEffect(() => {
    api.get("/budget").then(({ data }) => {
      setForm({
        bourse: data.bourse || "", job: data.job || "", famille: data.famille || "",
        apl: data.apl || "", autres: data.autres || "",
      });
    }).catch(() => {});
  }, []);

  const num = (v) => (parseFloat(v) || 0);
  const total = FIELDS.reduce((s, f) => s + num(form[f.key]), 0);

  const needs = total * 0.5;
  const wants = total * 0.3;
  const savings = total * 0.2;

  const donut = [
    { name: t(B.needs, lang), value: needs, color: "#22314F" },
    { name: t(B.wants, lang), value: wants, color: "#E8A33D" },
    { name: t(B.savings, lang), value: savings, color: "#3E8E72" },
  ];

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/budget", {
        bourse: num(form.bourse), job: num(form.job), famille: num(form.famille),
        apl: num(form.apl), autres: num(form.autres),
      });
      toast.success(t(B.saved, lang));
    } catch {
      toast.error(UI.common.error[lang]);
    } finally {
      setSaving(false);
    }
  };

  const tips = [];
  if (num(form.apl) === 0) tips.push({ id: "apl", node: B.tips.apl });
  tips.push({ id: "restou", node: B.tips.restou });
  tips.push({ id: "transport", node: B.tips.transport });
  if (num(form.job) > 0) tips.push({ id: "job", node: B.tips.job });
  tips.push({ id: "mutuelle", node: B.tips.mutuelle });
  tips.push({ id: "banque", node: B.tips.banque });
  tips.push({ id: "bonsplans", node: B.tips.bonsplans });

  return (
    <div className="animate-fade-up space-y-5">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{t(B.title, lang)}</h1>
        <p className="text-sm text-ink/60 mt-1">{t(B.intro, lang)}</p>
      </div>

      {/* Resources */}
      <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm">
        <h2 className="font-display text-lg font-bold mb-3">{t(B.resources, lang)}</h2>
        <div className="space-y-2.5">
          {FIELDS.map(({ key, Icon }) => (
            <div key={key} className="flex items-center gap-3 rounded-2xl border border-line bg-paper px-3.5 py-2.5">
              <span className="grid place-items-center w-8 h-8 rounded-xl bg-amber/15 shrink-0">
                <Icon className="w-4 h-4 text-amber" strokeWidth={2.2} />
              </span>
              <label className="flex-1 text-sm font-medium">{t(B[key], lang)}</label>
              <div className="flex items-center gap-1">
                <input
                  data-testid={`budget-${key}`}
                  type="number"
                  min="0"
                  max="100000"
                  inputMode="decimal"
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  placeholder="0"
                  className="w-20 text-right bg-transparent outline-none text-sm font-semibold"
                />
                <span className="text-ink/50 text-sm">€</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-line">
          <span className="text-sm font-semibold text-ink/70">{t(B.total, lang)}</span>
          <span data-testid="budget-total" className="font-display text-2xl font-extrabold text-ink">
            {money.format(total)} <span className="text-sm font-medium text-ink/50">{t(B.perMonth, lang)}</span>
          </span>
        </div>

        <button
          data-testid="budget-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full mt-4 rounded-2xl bg-amber text-ink font-bold py-3.5 flex items-center justify-center gap-2 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {t(B.save, lang)}
        </button>
      </div>

      {total <= 0 ? (
        <div className="rounded-[18px] border border-dashed border-line bg-white/50 p-8 text-center">
          <Wallet className="w-8 h-8 mx-auto text-ink/30 mb-3" />
          <p className="text-sm text-ink/50">{t(B.empty, lang)}</p>
        </div>
      ) : (
        <>
          {/* 50/30/20 */}
          <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm" data-testid="budget-split">
            <h2 className="font-display text-lg font-bold mb-3">{t(B.ruleTitle, lang)}</h2>
            <div className="relative h-52">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut} dataKey="value" innerRadius={62} outerRadius={92} paddingAngle={3} stroke="none">
                    {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs text-ink/50 font-medium">{t(B.total, lang)}</span>
                <span className="font-display text-xl font-extrabold">{money.format(total)}</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <SplitCard label={t(B.needs, lang)} pct="50 %" amount={money.format(needs)} color="#22314F" />
              <SplitCard label={t(B.wants, lang)} pct="30 %" amount={money.format(wants)} color="#E8A33D" />
              <SplitCard label={t(B.savings, lang)} pct="20 %" amount={money.format(savings)} color="#3E8E72" />
            </div>
          </div>

          {/* Needs breakdown */}
          <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm" data-testid="needs-breakdown">
            <h2 className="font-display text-lg font-bold mb-1">{t(B.needsBreakdown, lang)}</h2>
            <p className="text-xs text-ink/50 mb-3">{t(B.needs, lang)} · {money.format(needs)}</p>
            <div className="space-y-2">
              {NEEDS.map(({ key, pct, Icon, color }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="grid place-items-center w-8 h-8 rounded-xl shrink-0" style={{ background: `${color}18` }}>
                    <Icon className="w-4 h-4" style={{ color }} strokeWidth={2.2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{t(B.needsItems[key], lang)}</span>
                      <span className="font-semibold">{money.format(needs * pct)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct * 100}%`, background: color }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Optimization tips */}
      <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm" data-testid="budget-tips">
        <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
          <Lightbulb className="w-5 h-5 text-amber" /> {t(B.tipsTitle, lang)}
        </h2>
        <div className="space-y-2.5">
          {tips.map((tip) => (
            <div key={tip.id} data-testid={`tip-${tip.id}`} className="flex gap-3 rounded-2xl bg-paper border border-line p-3.5">
              <ShoppingBag className="w-4 h-4 text-sage shrink-0 mt-0.5" />
              <p className="text-[13px] text-ink/75 leading-relaxed">{t(tip.node, lang)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SplitCard({ label, pct, amount, color }) {
  return (
    <div className="rounded-2xl border border-line p-3 text-center">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <span className="text-xs font-semibold text-ink/60">{label}</span>
      </div>
      <div className="font-display text-base font-extrabold leading-none">{amount}</div>
      <div className="text-[11px] text-ink/40 mt-0.5">{pct}</div>
    </div>
  );
}
